/**
 * Domain Configuration Context
 * Manages multi-domain firm configuration for OMS Portal
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Domain Config Context
const DomainContext = createContext(null);

// Initial state
const initialConfig = {
  isLoading: true,
  isMainDomain: true,
  isUndefinedDomain: false,
  firmId: null,
  firmName: null,
  logoUrl: null,
  siteTitle: 'OMS Portal',
  siteDescription: 'İnsan Kaynakları Yönetim Sistemi',
  allowRegistration: false,
  domain: window.location.hostname,
  error: null
};

/**
 * Domain Provider Component
 * Wraps the app to provide domain configuration
 */
export const DomainProvider = ({ children }) => {
  const [config, setConfig] = useState(initialConfig);

  const fetchDomainConfig = useCallback(async () => {
    try {
      // Browser automatically sets Origin header, no need to set manually
      const response = await axios.get(`${API_URL}/api/config/domain`);

      if (response.data.success) {
        const data = response.data.data;
        
        setConfig({
          isLoading: false,
          isMainDomain: data.is_main_domain,
          isUndefinedDomain: false,
          firmId: data.firm_id,
          firmName: data.firm_name,
          logoUrl: data.logo_url,
          siteTitle: data.site_title || 'OMS Portal',
          siteDescription: data.site_description || '',
          allowRegistration: data.allow_registration,
          domain: data.domain,
          error: null
        });

        // Update document title
        document.title = data.site_title || 'OMS Portal';
        
        // Update meta description
        updateMetaDescription(data.site_description);
        
      } else {
        // Undefined domain
        setConfig({
          ...initialConfig,
          isLoading: false,
          isUndefinedDomain: true,
          error: response.data.message || 'Tanımsız domain'
        });
      }
    } catch (error) {
      console.error('Failed to fetch domain config:', error);
      
      // On error, assume main domain for development
      setConfig({
        ...initialConfig,
        isLoading: false,
        isMainDomain: true,
        error: null
      });
    }
  }, []);

  useEffect(() => {
    fetchDomainConfig();
  }, [fetchDomainConfig]);

  // Refresh config method (useful after firm domain changes)
  const refreshConfig = useCallback(() => {
    setConfig(prev => ({ ...prev, isLoading: true }));
    fetchDomainConfig();
  }, [fetchDomainConfig]);

  return (
    <DomainContext.Provider value={{ ...config, refreshConfig }}>
      {children}
    </DomainContext.Provider>
  );
};

/**
 * Hook to access domain configuration
 */
export const useDomain = () => {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error('useDomain must be used within a DomainProvider');
  }
  return context;
};

/**
 * Update meta description tag
 */
const updateMetaDescription = (description) => {
  if (!description) return;
  
  let metaTag = document.querySelector('meta[name="description"]');
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = 'description';
    document.head.appendChild(metaTag);
  }
  
  metaTag.content = description;
};

/**
 * Check if registration is allowed for current domain
 */
export const canRegister = (domainConfig) => {
  if (!domainConfig) return false;
  if (domainConfig.isUndefinedDomain) return false;
  return domainConfig.allowRegistration;
};

export default DomainContext;
